import { useMemo, useState, useCallback } from "react";
import PageContainer from "../components/layout/PageContainer";
import Button from "../components/common/Button";
import AdminCalendarWeek from "../components/calendar/AdminCalendarWeek";

function getDefaultView() {
  if (typeof window === "undefined") return "month";
  // Agenda is a vertical list — much better than the grid on phones.
  return window.innerWidth < 768 ? "agenda" : "month";
}

export default function AdminCalendar() {
  const [view, setView] = useState(getDefaultView); // month | week | day | agenda
  const [statusFilter, setStatusFilter] = useState("all"); // all | pending | confirmed | canceled
  const [serviceFilter, setServiceFilter] = useState("all"); // all | <id>
  const [serviceOptions, setServiceOptions] = useState([]); // array of ids as strings
  const [triggerNewBooking, setTriggerNewBooking] = useState(false); // trigger for new booking
  const [currentEvents, setCurrentEvents] = useState({ events: [], bookings: [] }); // current events from calendar

  // Memoize the events update callback to prevent infinite re-renders
  const onEventsUpdate = useCallback((events, bookings) => {
    setCurrentEvents({ events, bookings });
  }, []);

  // A "jump to date" control: store as YYYY-MM-DD
  const todayStr = useMemo(() => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }, []);
  const [jumpDate, setJumpDate] = useState(todayStr);

  return (
    <PageContainer>
      {/* Modern Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-birdie-charcoal">Calendar</h1>
          <p className="mt-1 text-birdie-charcoal/70">Schedule and manage all your bookings in one place</p>
        </div>

        <div className="flex items-center gap-3">
          <Button variant="secondary" onClick={() => (window.location.href = "/admin")} className="gap-2">
            <DashboardIcon />
            Dashboard
          </Button>
        </div>
      </div>

      {/* Controls Toolbar */}
      <div className="mt-6 rounded-xl bg-white border border-birdie-green/15 p-3 sm:p-4">
        <div className="flex flex-col gap-3 sm:gap-4">
          {/* Top Row: View Toggle + Date Jump */}
          <div className="flex items-center justify-between">
            {/* View Toggle */}
            <div className="flex items-center gap-1 bg-birdie-beige rounded-lg p-1 overflow-x-auto">
              {[
                { key: "agenda", label: "List", Icon: ListIcon },
                { key: "month", label: "Month", Icon: CalendarIcon },
                { key: "week", label: "Week", Icon: WeekIcon, hideMobile: true },
                { key: "day", label: "Day", Icon: DayIcon, hideMobile: true },
              ].map(({ key, label, Icon, hideMobile }) => (
                <button
                  key={key}
                  onClick={() => setView(key)}
                  className={`${hideMobile ? "hidden sm:inline-flex" : "inline-flex"} items-center gap-1.5 px-2.5 sm:px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                    view === key
                      ? "bg-white text-birdie-charcoal shadow-sm"
                      : "text-birdie-charcoal/60 hover:text-birdie-charcoal"
                  }`}
                >
                  <Icon size={14} />
                  <span>{label}</span>
                </button>
              ))}
            </div>

            {/* Date Jump */}
            <div className="flex items-center gap-1 sm:gap-2">
              <button
                onClick={() => setJumpDate(todayStr)}
                className="px-2 sm:px-3 py-1.5 text-sm font-medium text-birdie-green hover:bg-birdie-cream rounded-md transition-colors"
              >
                Today
              </button>
              <input
                type="date"
                className="w-[130px] sm:w-auto border border-birdie-green/15 rounded-md px-2 py-1.5 text-sm text-birdie-charcoal focus:border-birdie-green focus:outline-none focus:ring-1 focus:ring-birdie-mustard/40"
                value={jumpDate}
                onChange={(e) => setJumpDate(e.target.value)}
              />
            </div>
          </div>

          {/* Bottom Row: Filters (responsive) */}
          <div className="flex items-center gap-2 sm:gap-4 text-sm overflow-x-auto">
            <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
              <span className="text-birdie-charcoal/60 hidden sm:inline">Status:</span>
              <select
                className="bg-birdie-beige sm:bg-transparent border-0 rounded-md sm:rounded-none text-birdie-charcoal font-medium focus:ring-0 cursor-pointer py-1.5 pl-2 pr-6 text-sm"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="quoted">Quoted</option>
                <option value="confirmed">Confirmed</option>
                <option value="canceled">Canceled</option>
              </select>
            </div>

            <span className="text-birdie-warmgray hidden sm:inline">|</span>

            <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
              <span className="text-birdie-charcoal/60 hidden sm:inline">Service:</span>
              <select
                className="bg-birdie-beige sm:bg-transparent border-0 rounded-md sm:rounded-none text-birdie-charcoal font-medium focus:ring-0 cursor-pointer py-1.5 pl-2 pr-6 text-sm"
                value={serviceFilter}
                onChange={(e) => setServiceFilter(e.target.value)}
              >
                <option value="all">All Services</option>
                {serviceOptions.map((id) => (
                  <option key={id} value={id}>Service #{id}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-4">
        {/* Main Calendar Area */}
        <div className="lg:col-span-3">
          <AdminCalendarWeek
            view={view}
            statusFilter={statusFilter}
            serviceFilter={serviceFilter}
            jumpDate={jumpDate}
            triggerNewBooking={triggerNewBooking}
            onServiceOptions={(ids) => setServiceOptions(ids || [])}
            onNewBookingHandled={() => setTriggerNewBooking(false)}
            onEventsUpdate={onEventsUpdate}
          />
        </div>

        {/* Calendar Insights Sidebar */}
        <div className="space-y-6">
          <CalendarInsights 
            view={view}
            statusFilter={statusFilter}
            serviceFilter={serviceFilter}
            currentEvents={currentEvents}
            onNewBooking={() => setTriggerNewBooking(true)}
          />
        </div>
      </div>
    </PageContainer>
  );
}

// Calendar Insights Sidebar Component
function CalendarInsights({ view, statusFilter, serviceFilter, currentEvents, onNewBooking }) {
  return (
    <>
      {/* Quick Actions */}
      <div className="rounded-2xl border-0 bg-white p-6 shadow-lg">
        <h3 className="font-accent text-lg font-semibold text-birdie-charcoal mb-4">Quick Actions</h3>
        <div className="space-y-3">
          <Button
            onClick={onNewBooking}
            className="w-full justify-start gap-3 bg-birdie-green hover:bg-birdie-green-dark"
          >
            <PlusIcon />
            New Booking
          </Button>
          <Button 
            variant="secondary" 
            onClick={() => (window.location.href = "/admin")} 
            className="w-full justify-start gap-3"
          >
            <DashboardIcon />
            View Dashboard
          </Button>
          <Button 
            variant="secondary" 
            onClick={() => window.location.reload()} 
            className="w-full justify-start gap-3"
          >
            <RefreshIcon />
            Refresh Calendar
          </Button>
        </div>
      </div>

      {/* Events List */}
      <EventsList 
        events={currentEvents.events}
        bookings={currentEvents.bookings}
        view={view}
      />

      {/* Current View Info */}
      <div className="rounded-2xl border-0 bg-gradient-to-br from-birdie-cream to-birdie-beige p-6 shadow-lg">
        <h3 className="font-accent text-lg font-semibold text-birdie-charcoal mb-4">Current View</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-birdie-charcoal/70">Display mode</span>
            <span className="font-semibold text-birdie-green capitalize">{view}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-birdie-charcoal/70">Status filter</span>
            <span className={`text-sm font-medium ${statusFilter === 'all' ? 'text-birdie-charcoal/70' : 'text-birdie-green'}`}>
              {statusFilter === 'all' ? 'All' : statusFilter}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-birdie-charcoal/70">Service filter</span>
            <span className={`text-sm font-medium ${serviceFilter === 'all' ? 'text-birdie-charcoal/70' : 'text-birdie-green'}`}>
              {serviceFilter === 'all' ? 'All' : `#${serviceFilter}`}
            </span>
          </div>
        </div>
      </div>

      {/* Calendar Tips */}
      <div className="rounded-2xl border-0 bg-white p-6 shadow-lg">
        <h3 className="font-accent text-lg font-semibold text-birdie-charcoal mb-4">Calendar Tips</h3>
        <div className="space-y-3 text-sm text-birdie-charcoal/70">
          <div className="flex items-start gap-2">
            <span className="text-birdie-green mt-0.5">💡</span>
            <span>Click on any time slot to create a new booking</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="text-birdie-green mt-0.5">👆</span>
            <span>Click on existing events to view or edit details</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="text-birdie-green mt-0.5">🎯</span>
            <span>Use filters to focus on specific booking types</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="text-birdie-mustard-dark mt-0.5">⚡</span>
            <span>Calendar refreshes when you return to this tab</span>
          </div>
        </div>
      </div>
    </>
  );
}

// Events List Component
function EventsList({ events, bookings, view }) {
  const sortedBookings = useMemo(() => {
    if (!bookings || bookings.length === 0) return [];
    
    const sorted = [...bookings].sort((a, b) => new Date(a.start_at) - new Date(b.start_at));
    return sorted.slice(0, 12); // Show max 12 events
  }, [bookings]);

  return (
    <div className="rounded-2xl border-0 bg-white p-6 shadow-lg">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-accent text-lg font-semibold text-birdie-charcoal">Events in View</h3>
        <span className="text-sm text-birdie-charcoal/60 capitalize">{view} view</span>
      </div>

      {sortedBookings.length === 0 ? (
        <div className="text-center py-8">
          <div className="text-4xl mb-3">📅</div>
          <div className="text-birdie-charcoal/60 font-medium">No events found</div>
          <div className="text-sm text-birdie-warmgray mt-1">No bookings in current view</div>
        </div>
      ) : (
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {sortedBookings.map((booking) => {
            const startTime = new Date(booking.start_at);
            const endTime = new Date(booking.end_at);
            const isToday = startTime.toDateString() === new Date().toDateString();
            const isPast = startTime < new Date();
            
            return (
              <div key={booking.id} className={`relative flex gap-3 p-4 rounded-xl border-l-4 ${
                booking.status === 'confirmed' ? 'border-l-birdie-green bg-birdie-green/10' :
                booking.status === 'canceled' ? 'border-l-red-500 bg-red-50' :
                'border-l-birdie-mustard bg-birdie-mustard/10'
              } ${isPast ? 'opacity-60' : ''} hover:shadow-md transition-all duration-200 cursor-pointer`}>

                {/* Time Column */}
                <div className="flex flex-col items-center min-w-[70px] text-center">
                  <div className={`text-xs font-medium ${isToday ? 'text-birdie-green' : 'text-birdie-charcoal/60'}`}>
                    {startTime.toLocaleDateString([], { month: 'short', day: 'numeric' })}
                  </div>
                  <div className="text-sm font-bold text-birdie-charcoal">
                    {startTime.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                  </div>
                  <div className="text-xs text-birdie-charcoal/60">
                    {endTime.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-birdie-charcoal truncate">{booking.customer_name}</h4>
                      <p className="text-sm text-birdie-charcoal/70 truncate">{booking.service_name}</p>
                      {booking.units && booking.units > 1 && (
                        <p className="text-xs text-birdie-charcoal/60">{booking.units} units</p>
                      )}
                      {booking.location_address && (
                        <p className="text-xs text-birdie-charcoal/60 mt-1 flex items-center gap-1 truncate">
                          📍 {booking.location_address}
                        </p>
                      )}
                    </div>
                    <div className="ml-2 flex-shrink-0">
                      <CalendarEventStatusPill status={booking.status} />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          
          {bookings && bookings.length > 12 && (
            <div className="text-center py-2">
              <span className="text-xs text-birdie-charcoal/60">
                +{bookings.length - 12} more events • Use filters to narrow down
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CalendarEventStatusPill({ status }) {
  const configs = {
    confirmed: {
      bg: "bg-birdie-green/10",
      text: "text-birdie-green",
      label: "✓"
    },
    canceled: {
      bg: "bg-red-100",
      text: "text-red-800",
      label: "✕"
    },
    pending: {
      bg: "bg-birdie-mustard/20",
      text: "text-birdie-mustard-dark",
      label: "⏳"
    },
  };

  const config = configs[status] || configs.pending;

  return (
    <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
}

// SVG Icons
function ListIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="8" y1="6" x2="21" y2="6" />
      <line x1="8" y1="12" x2="21" y2="12" />
      <line x1="8" y1="18" x2="21" y2="18" />
      <line x1="3" y1="6" x2="3.01" y2="6" />
      <line x1="3" y1="12" x2="3.01" y2="12" />
      <line x1="3" y1="18" x2="3.01" y2="18" />
    </svg>
  );
}

function CalendarIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
      <line x1="16" y1="2" x2="16" y2="6"/>
      <line x1="8" y1="2" x2="8" y2="6"/>
      <line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
  );
}

function WeekIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
      <line x1="7" y1="8" x2="7" y2="16"/>
      <line x1="11" y1="8" x2="11" y2="16"/>
      <line x1="15" y1="8" x2="15" y2="16"/>
      <line x1="19" y1="8" x2="19" y2="16"/>
    </svg>
  );
}

function DayIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
      <line x1="16" y1="2" x2="16" y2="6"/>
      <line x1="8" y1="2" x2="8" y2="6"/>
      <line x1="3" y1="10" x2="21" y2="10"/>
      <circle cx="12" cy="15" r="2"/>
    </svg>
  );
}

function PlusIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="12" y1="5" x2="12" y2="19"/>
      <line x1="5" y1="12" x2="19" y2="12"/>
    </svg>
  );
}

function DashboardIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="3" width="7" height="7"/>
      <rect x="14" y="3" width="7" height="7"/>
      <rect x="14" y="14" width="7" height="7"/>
      <rect x="3" y="14" width="7" height="7"/>
    </svg>
  );
}

function RefreshIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
      <path d="M21 3v5h-5"/>
      <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
      <path d="M3 21v-5h5"/>
    </svg>
  );
}

